import React, { useState } from 'react';
import { Collapse, Spinner } from 'react-bootstrap';
import { Flex, Grid, Image, Text } from '@/components/common';
import { ChevronRight } from '@/assets/icons';
import styled from 'styled-components';
import { otherColors } from '@/styles/colors';

interface IAccordionProps {
  icon?: JSX.Element;
  title: string;
  children?: JSX.Element | JSX.Element[];
  rightNumber?: number;
  padding?: string;
  isOpen?: boolean;
  showLoader?: boolean;
}

const Accordion: React.FC<IAccordionProps> = ({
  icon,
  title,
  children,
  rightNumber,
  padding,
  isOpen,
  showLoader,
}: IAccordionProps) => {
  const [open, setOpen] = useState(isOpen);
  return (
    <>
      <Flex padding={padding ? padding : '8px 12px'} columnGap="16px" onClick={() => setOpen(!open)}>
        {icon}
        {children && (
          <Image
            className="ms-2"
            style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: '0.3s transform' }}
            width="8px"
            src={ChevronRight}
            alt=""
          />
        )}
        <Text fontSize="16px">{title}</Text>

        {showLoader ? (
          <Text className="ms-auto" fontSize="13px" fontWeight={600}>
            <Spinner className="m-auto" animation="border" variant="primary" />
          </Text>
        ) : (
          Number.isInteger(rightNumber) && (
            <Text className="ms-auto" fontSize="13px" fontWeight={600}>
              {rightNumber}
            </Text>
          )
        )}
      </Flex>
      {children && (
        <Collapse in={open}>
          <div>{children}</div>
        </Collapse>
      )}
    </>
  );
};

interface IAccordionItemProps {
  children: string;
  rightNumber?: number;
  gutterLeft: number;
  applicantDetails?: {
    applicationStatusId: number;
    countByRange: {
      firstRangeCount: number;
      lastRangeCount: number;
      secondRangeCount: number;
    };
    totalCount: number;
  };
  selected?: boolean;
  onClick?: () => void;
  showLoader?: boolean;
}

const getColoredBox = (count: number, color: string): React.ReactElement => {
  return (
    <ColoredBox background={!count ? 'none' : color} height="25px">
      <span>{!count ? '' : count}</span>
    </ColoredBox>
  );
};

const AccordionItem: React.FC<IAccordionItemProps> = ({
  selected,
  rightNumber,
  applicantDetails,
  children,
  gutterLeft,
  onClick,
  showLoader,
}: IAccordionItemProps) => (
  <Flex
    background={selected ? otherColors.live_blue : undefined}
    cursor="pointer"
    padding={`8px 12px 8px ${12 + (gutterLeft ?? 0)}px`}
    columnGap="12px"
    onClick={onClick}
  >
    <Grid width="35%">
      <Text fontSize="16px" color={selected ? otherColors.white : undefined} whiteSpace="nowrap">
        {children}
      </Text>
    </Grid>
    <Flex columnGap="6px" width="100%" margin="0 auto" alignItems="center" justifyContent="end">
      {!showLoader && applicantDetails && applicantDetails.countByRange && (
        <>
          {getColoredBox(applicantDetails.countByRange.firstRangeCount, otherColors.success_green)}
          {getColoredBox(applicantDetails.countByRange.secondRangeCount, otherColors.orange)}
          {getColoredBox(applicantDetails.countByRange.lastRangeCount, otherColors.danger_red)}
        </>
      )}
    </Flex>
    <Grid alignItems="center">
      {showLoader ? (
        <Text className="ms-auto" fontSize="13px" fontWeight={600}>
          <Spinner className="m-auto" animation="border" variant={selected ? 'light' : 'primary'} />
        </Text>
      ) : (
        Number.isInteger(rightNumber) && (
          <Text className="ms-auto" fontSize="13px" fontWeight={600}>
            {rightNumber}
          </Text>
        )
      )}
    </Grid>
  </Flex>
);

export { AccordionItem, Accordion };

export const ColoredBox = styled.div<{
  width?: string;
  height?: string;
  background: string;
  color?: string;
  margin?: string;
}>`
  width: ${props => props.width};
  height: ${props => props.height};
  background: ${props => props.background};
  color: ${props => props.color ?? otherColors.white};
  margin: ${props => props.margin};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0 8%;
  min-width: 20px;
  font-size: 13px;
`;
